import { Field, Form, Formik } from 'formik';
import { useRouter } from '@uirouter/react';
import { GitBranch } from 'lucide-react';

import { AuthTypeOption } from '@/react/portainer/account/git-credentials/types';

import { FormControl } from '@@/form-components/FormControl';
import { Input } from '@@/form-components/Input';
import { LoadingButton } from '@@/buttons/LoadingButton';
import { Button } from '@@/buttons';
import { BoxSelector, BoxSelectorOption } from '@@/BoxSelector';

import {
  CredentialType,
  credentialTitles,
  GitCredentialFormValues,
} from '../../types';

import { validationSchema } from './GitCredentialsForm.validation';

type Props = {
  selectedProvider: CredentialType;
  isEditing?: boolean;
  isLoading: boolean;
  onSubmit: (formValues: GitCredentialFormValues) => void;
  credentialNames: string[];
  initialValues?: GitCredentialFormValues;
};

const defaultInitialValues = {
  name: '',
  credentials: {
    username: '',
    authType: AuthTypeOption.Basic,
    personalAccessToken: '',
  },
};

const authOptions: BoxSelectorOption<AuthTypeOption>[] = [
  {
    id: 'basic',
    icon: GitBranch,
    iconType: 'badge',
    label: 'Basic',
    value: AuthTypeOption.Basic,
  },
  {
    id: 'token',
    icon: GitBranch,
    iconType: 'badge',
    label: 'Token',
    value: AuthTypeOption.Token,
  },
];

export function GitCredentialsForm({
  selectedProvider,
  isEditing = false,
  isLoading,
  onSubmit,
  credentialNames,
  initialValues = defaultInitialValues,
}: Props) {
  const router = useRouter();
  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validationSchema={() => validationSchema(credentialNames)}
      onSubmit={(values) => onSubmit(values)}
      validateOnMount
    >
      {({ values, errors, handleSubmit, isValid, dirty, setFieldValue }) => (
        <Form className="form-horizontal" onSubmit={handleSubmit} noValidate>
          {isEditing && (
            <div>
              <FormControl inputId="provider" label="Provider" size="medium">
                <Field
                  as={Input}
                  disabled
                  name="provider"
                  autoComplete="off"
                  id="provider"
                  value={credentialTitles[selectedProvider]}
                  data-cy="cloudSettings-provider"
                />
              </FormControl>
              <FormControl
                inputId="authType"
                label="Authorization type"
                size="medium"
              >
                <Field
                  as={Input}
                  disabled
                  name="authType"
                  autoComplete="off"
                  id="authType"
                  value={authOptions[values.credentials.authType].label}
                  data-cy="cloudSettings-authType"
                />
              </FormControl>
            </div>
          )}
          {!isEditing && (
            <BoxSelector
              radioName="authType"
              options={authOptions}
              value={values.credentials.authType}
              onChange={(value) => setFieldValue('credentials.authType', value)}
              slim
            />
          )}
          <FormControl
            inputId="name"
            label="Credentials name"
            size="medium"
            errors={errors.name}
            required
          >
            {' '}
            <Field
              as={Input}
              name="name"
              id="name"
              maxLength={200}
              value={values.name}
              placeholder={`e.g. ${credentialTitles[selectedProvider]}-staging`}
              data-cy="cloudSettings-apiKeyName"
            />
          </FormControl>
          <FormControl
            inputId="git_username"
            label="Git username"
            size="medium"
            errors={errors.credentials?.username}
            required
          >
            <Field
              as={Input}
              name="credentials.username"
              autoComplete="off"
              id="git_username"
              maxLength={200}
              value={values.credentials.username}
              placeholder="e.g. git_builder"
              data-cy="cloudSettings-GitUsername"
            />
          </FormControl>
          {(values.credentials.authType === AuthTypeOption.Token ||
            values.credentials.authType === AuthTypeOption.Basic) && (
            <FormControl
              inputId="git_personalAccessToken"
              label="Personal Access Token"
              size="medium"
              errors={errors.credentials?.personalAccessToken}
              required
            >
              <Field
                as={Input}
                name="credentials.personalAccessToken"
                autoComplete="off"
                type="password"
                id="git_personalAccessToken"
                maxLength={200}
                value={values.credentials.personalAccessToken}
                placeholder={isEditing ? '*******' : 'e.g. XXXXXXX'}
                data-cy="cloudSettings-GitPAT"
              />
            </FormControl>
          )}
          <div className="form-group">
            <div className="col-sm-12 mt-3">
              <LoadingButton
                disabled={!isValid || !dirty}
                data-cy="createCredentials-saveButton"
                isLoading={isLoading}
                className="!ml-0"
                loadingText="Saving credentials..."
              >
                {isEditing ? 'Update credentials' : 'Add credentials'}
              </LoadingButton>
              {isEditing && (
                <Button
                  color="default"
                  data-cy="createGitCredentials-cancelButton"
                  onClick={() =>
                    router.stateService.go(
                      'portainer.settings.sharedcredentials'
                    )
                  }
                >
                  Cancel
                </Button>
              )}
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
}
